import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { FilterIcon, NewHotelIcon, SearchIcon } from "../../../../helper/HwIcon";
import { ApiCall } from "../../../../helper/axios";
import { toUrlVendor } from "../../../../helper/vendorMask";
import { useFilterHotels } from "../../hooks/useFilterHotels";
import { defaultFilterSelection } from "../../hooks/useGetListHotels";

const LocationSearch = ({ cityId, queryString, cityName }) => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const fetchOptions = async (kw) => {
    if (!cityId) return;
    setLoading(true);
    try {
      const response = await ApiCall(
        "GET",
        `/hotels/search-by-region?cityId=${cityId}&keyword=${encodeURIComponent(kw)}`,
      );
      setOptions(response?.data?.data?.hotels ?? []);
    } catch (error) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setKeyword(value);
    setOpen(true);
    clearTimeout(debounceRef.current);
    if (!value || value.trim().length < 2) {
      setOptions([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchOptions(value.trim()), 400);
  };

  const handleSelect = (hotel) => {
    if (!hotel?.id) return;
    const params = new URLSearchParams(queryString || "");
    if (hotel?.hid !== null && hotel?.hid !== undefined) {
      params.set("hid", hotel.hid);
    }
    params.set("vendor", toUrlVendor(hotel?.vendor || "RATEHAWK"));
    params.set("commonId", "");
    setOpen(false);
    setKeyword(hotel?.name || "");
    navigate(`/hotels/details/${hotel.id}?${params.toString()}`);
  };

  return (
    <div
      className="hw-hotel--locationSearch"
      ref={boxRef}
      style={{ position: "relative" }}
    >
      <div className="hw-hotel--filterSearch">
        <input
          onChange={handleChange}
          onFocus={() => keyword && setOpen(true)}
          value={keyword}
          placeholder={cityName ? `Search Hotels in ${cityName}` : "Search Hotels"} type="text"
          id="hotelLocation"
          className="hw-form--input"
          autoComplete="off"
        />
        <SearchIcon />
      </div>
      {open && keyword?.trim().length >= 2 && (
        <ul
          className="list-unstyled hw-hotel--locationSearch__dropdown"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            zIndex: 20,
            margin: 0,
            padding: "4px 0",
            maxHeight: "300px",
            overflowY: "auto",
            background: "#fff",
            border: "1px solid #e2e6ee",
            borderRadius: "8px",
            boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
          }}
        >
          {loading ? (
            <li style={{ padding: "10px 14px", color: "#7a8699" }}>Searching…</li>
          ) : options.length ? (
            options.map((hotel) => (
              <li
                key={hotel?.id || hotel?.hid}
                onClick={() => handleSelect(hotel)}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={(event) =>
                  (event.currentTarget.style.background = "#f4f7fb")
                }
                onMouseLeave={(event) =>
                  (event.currentTarget.style.background = "#fff")
                }
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "10px 14px",
                  cursor: "pointer",
                }}
              >
                <span
                  style={{
                    position: "static",
                    flexShrink: 0,
                    width: "18px",
                    height: "18px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginTop: "2px",
                    color: "#0062FF"
                  }}
                >
                  <NewHotelIcon width={24} height={24} style={{ minWidth: 24, minHeight: 24 }} />
                </span>
                <span className="hw-text14 hw-fontWeight--500" style={{ lineHeight: 1.3, color: "#555555" }}>
                  {hotel?.name}
                  {hotel?.cityName ? `, ${hotel.cityName}` : ""}
                </span>
              </li>
            ))
          ) : (
            <li style={{ padding: "10px 14px", color: "#7a8699" }}>
              No hotels found in this location. Try changing the city or region.
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

const RenderPriceFilterList = ({ type, data, label, setSelected, selected, }) => {
  return (
    <div className="hw-search--flightFilter__widget hotel-star--widget">
      <div className="d-flex align-items-center justify-between hw-search--flightFilter__title">
        <span className="hw-fontBold">{label}</span>
      </div>
      <div className="hw-search--flightFilter__widgetContent">
        <ul className="list-unstyled hw-hotel--Checkbox">
          {data?.length
            ? data.map((item) => {
              const key = `${item.start}-${item.end}`;
              const isSelected = selected?.[type]?.[key]?.selected;
              return (
                <li key={key} style={item.disabled ? { opacity: 0.4, pointerEvents: "none" } : undefined}>
                  <label>
                    <input
                      type="checkbox"
                      name={type}
                      checked={isSelected}
                      disabled={item.disabled}
                      onChange={() =>
                        setSelected({
                          ...selected,
                          [type]: {
                            ...(selected?.[type] || {}),
                            [key]: {
                              selected: !isSelected,
                              d: item,
                            },
                          },
                        })
                      }
                    />
                    <span className="hw-gray-900 overflow-ellipsis">
                      {item.name}
                    </span>
                  </label>
                  <span className="hw-text14 flex-shrink--null">
                    ({item.count})
                  </span>
                </li>
              );
            })
            : "Not Found Result"}
        </ul>
      </div>
    </div>
  );
};

const RenderFilterList = ({ type, data, label, setSelected, selected }) => {
  return (
    <div className="hw-search--flightFilter__widget hotel-star--widget">
      <div className="d-flex align-items-center justify-between hw-search--flightFilter__title">
        <span className="hw-fontBold">{label}</span>
      </div>
      <div className="hw-search--flightFilter__widgetContent">
        <ul className="list-unstyled hw-hotel--Checkbox">
          {data?.length
            ? data.map((item) => {
              const key = String(item.name);
              const isSelected = selected?.[type]?.[key]?.selected;
              return (
                <li key={key}>
                  <label>
                    <input
                      type="checkbox"
                      name={type}
                      value={item.name}
                      checked={isSelected}
                      onChange={() =>
                        setSelected({
                          ...selected,
                          [type]: {
                            ...(selected?.[type] || {}),
                            [key]: {
                              selected: !isSelected,
                              d: item,
                            },
                          },
                        })
                      }
                    />
                    <span
                      className="hw-gray-900 overflow-ellipsis"
                      style={{ textTransform: "capitalize" }}
                    >
                      {item.name} {type === "ratings" ? "Star" : ""}
                    </span>
                  </label>
                  <span className="hw-text14 flex-shrink--null">
                    ({item.count})
                  </span>
                </li>
              );
            })
            : "Not Found Result"}
        </ul>
      </div>
    </div>
  );
};

const FacetsComponent = ({
  hotels,
  loading,
  selected,
  setSelected,
  originalData = [],
  sort,
  setSort,
  cityId,
  queryString,
  cityName,
}) => {
  const {
    setIsFilterCollapsed,
    isFilterCollapsed,
    propertyTypes,
    ratings,
    reset,
    setReset,
    priceSelection,
  } = useFilterHotels({
    hotels,
    loading,
    selected,
    originalData,
  });

  const [hotelNameInput, setHotelNameInput] = useState(
    selected?.hotelName || "",
  );
  const [locationKey, setLocationKey] = useState(0);
  const debounceTimerRef = useRef(null);

  // Keep local input in sync when the filter is cleared externally (e.g. "Clear" button).
  useEffect(() => {
    if (!selected?.hotelName) {
      setHotelNameInput("");
    }
  }, [selected?.hotelName]);

  // Cleanup debounce timer on unmount to prevent setState on unmounted component.
  useEffect(() => {
    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleHotelNameChange = useCallback(
    (event) => {
      const value = event.target.value;
      setHotelNameInput(value);
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        setSelected({
          ...selected,
          hotelName: value,
        });
      }, 300);
    },
    [selected, setSelected],
  );

  const handleClear = useCallback(() => {
    setSelected(defaultFilterSelection);
    setReset((value) => value + 1);
    setSort({
      price: null,
      mostPopular: false,
    });
    setLocationKey((k) => k + 1);
  }, [setSelected, setReset, setSort]);

  const noHotels = !originalData || originalData.length === 0;

  return (
    <>
      <div className="hw-hotel--asideFilter__top">
        <div className="hw-hotel--filter__action">
          <div
            onClick={() => setIsFilterCollapsed(!isFilterCollapsed)}
            className="hw-hotel--filter__actionFilter d-flex align-items-center hw-gapy10"
          >
            <FilterIcon />
            <span className="hide-up900">
              {isFilterCollapsed ? "Close" : "Filters"}
            </span>
            <span className="hide-down900">Filters</span>
          </div>
          {hotels?.length > 0 && (
            //clear filters button
            <button onClick={handleClear} className="hw-sortby--clear" type="button">
              <X className="hw-sortby--clear-icon" size={13} />
              Clear
            </button>
          )}

        </div>
      </div>
      <div className="hw-hotel--filter__body">
        <div className="hw-search--flightFilter__body">
          {!noHotels && (
            <LocationSearch key={locationKey} cityId={cityId} queryString={queryString} cityName={cityName} />
          )}
          <RenderPriceFilterList
            data={priceSelection}
            label="Price"
            selected={selected}
            setSelected={setSelected}
            type="priceSelection"
          />
          <RenderFilterList
            data={ratings}
            label="Star Category"
            selected={selected}
            setSelected={setSelected}
            type="ratings"
          />
          <RenderFilterList
            data={propertyTypes}
            label="Property Type"
            selected={selected}
            setSelected={setSelected}
            type="propertyType"
          />
        </div>
      </div>
    </>
  );
};

export const Facets = React.memo(FacetsComponent);

const SortByFilterComponent = ({ sort, setSort, disable = false, onClear }) => {
  const handleSort = useCallback(
    (type) => {
      if (type === "popular") {
        setSort({
          ...sort,
          price: null,
          mostPopular: !sort.mostPopular,
        });
        return;
      }
      setSort({
        ...sort,
        price: type,
        mostPopular: false,
      });
    },
    [sort, setSort],
  );

  const handleClearSort = useCallback(() => {
    setSort({ price: null, mostPopular: false });
    if (onClear) onClear();
  }, [setSort, onClear]);

  const isSortActive = sort.mostPopular || sort.price !== null;

  const tabs = [
    { label: "Most Popular", type: "popular", active: sort.mostPopular },
    { label: "Price: Low to High", type: "ASC", active: sort.price === "ASC" },
    { label: "Price: High to Low", type: "DESC", active: sort.price === "DESC" },
  ];


  return (
    <div
      className="d-flex align-items-center hw-gapy10 mt-12"
      style={{ pointerEvents: disable ? "none" : "" }}
    >
      <span className="flex-shrink--null hw-black hw-fontBold" style={{ whiteSpace: "nowrap" }}>
        Sort By:
      </span>
      <div className="hw-sortby--strip">
        {tabs.map((tab) => (
          <button
            key={tab.type}
            type="button"
            className={`hw-sortby--tab${tab.active ? " active" : ""}`}
            onClick={() => handleSort(tab.type)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {isSortActive && (
        <button
          type="button"
          className="hw-sortby--clear"
          onClick={handleClearSort}
          title="Clear sort"
        >
          <X className="hw-sortby--clear-icon" size={13} />
          Clear
        </button>
      )}
    </div>
  );
};

export const SortByFilter = React.memo(SortByFilterComponent);
